import { apiClient } from './client';

/**
 * Wire shape for ROADMAP #35:
 *   - `/api/v1/admin/crash-reports`        (list, superadmin)
 *   - `/api/v1/admin/crash-reports/{id}`   (detail, superadmin —
 *     side-effecting: first GET flips downloaded_at + writes the
 *     tenant_event so the tenant_admin Dashboard sees "evaluation
 *     started")
 *   - `/api/v1/tenants/{tenant_id}/events` (tenant-admin / superadmin
 *     message log)
 */

export interface CrashReportSummary {
  id: string;
  tenant_id: string;
  device_id: string;
  /**
   * Denormalised snapshot of the device label as it was at the
   * moment the crash fired — the capture-app reads it from
   * `EnrolledIdentity.deviceLabel` at enqueue time and includes it
   * in the POST. Null on kiosks enrolled before the field existed.
   */
  device_label: string | null;
  app_version: string | null;
  last_session_id: string | null;
  captured_at: string;
  created_at: string;
  downloaded_at: string | null;
  downloaded_by_user_id: string | null;
  resolved_at: string | null;
  operator_note: string | null;
  tenant_slug: string | null;
  tenant_name: string | null;
}

export interface CrashReportDetail extends CrashReportSummary {
  stack_trace: string;
}

export interface ListCrashFilters {
  limit?: number;
  offset?: number;
  unread_only?: boolean;
}

export async function listCrashReports(
  filters: ListCrashFilters = {},
): Promise<CrashReportSummary[]> {
  const params: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== '' && v !== null) {
      params[k] = v;
    }
  }
  const { data } = await apiClient.get<CrashReportSummary[]>(
    '/api/v1/admin/crash-reports',
    { params },
  );
  return data;
}

/**
 * IMPORTANT: this GET is side-effecting on the backend (first call
 * sets `downloaded_at` and emits the `crash_report_downloaded`
 * tenant_event). Idempotent thereafter. Callers should treat
 * "opening the detail dialog" as the act of evaluating the report.
 */
export async function getCrashReport(id: string): Promise<CrashReportDetail> {
  const { data } = await apiClient.get<CrashReportDetail>(
    `/api/v1/admin/crash-reports/${id}`,
  );
  return data;
}

// ----------------------------------------------------------------------
// Tenant message-log feed
// ----------------------------------------------------------------------
export interface TenantEvent {
  id: string;
  tenant_id: string;
  created_at: string;
  event_type: string;
  entity_type: string | null;
  entity_id: string | null;
  message: string;
  metadata: Record<string, unknown>;
}

export interface TenantEventPage {
  items: TenantEvent[];
  total: number;
}

export interface ListTenantEventsFilters {
  limit?: number;
  offset?: number;
}

export async function listTenantEvents(
  tenantId: string,
  filters: ListTenantEventsFilters = {},
): Promise<TenantEventPage> {
  const params: Record<string, string | number> = {};
  for (const [k, v] of Object.entries(filters)) {
    if (v !== undefined && v !== '' && v !== null) {
      params[k] = v;
    }
  }
  const { data } = await apiClient.get<TenantEventPage>(
    `/api/v1/tenants/${tenantId}/events`,
    { params },
  );
  return data;
}
